import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { useAccountStore } from '../../../components/Stores/accountStore';
import { IBalanceFlowProps } from '../../../sp-types/balanceFlowType';
import { createEmptyRiskTolerance, getRiskTolerance, getSortedRouteIdsByMaterialId } from '../../../utils/accountUtils';

import { useNavigate } from 'react-router-dom';
import ButtonDesigned from '../../../components/UI-Elements/Buttons/ButtonDesigned';
import { LinearGauge } from '../../../components/UI-Elements/Charts/LinearGauge/LinearGauge';
import { getOpenBalanceFlow } from '../../../sp-firebase/balanceFlowFB';
import { IMaterialExposure } from '../../../sp-types/netExposureType';
import { getExposureColor, getExposureStatus } from '../../../utils/RiskUtils';
import { getFinancialPositionsSum } from '../balanceUtils';
import BalanceChartCard from './BalanceChartCard';
import { BalanceOverviewDetails } from './BalanceOverviewDetails';
import { MainCauseTitleSection } from './GeneralComponents/MainCauseTitleSection';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import { PopoverConfirmOverlay } from '../../../components/UI-Elements/PopoverOverlays/PopoverConfirmOverlay';
import { ConfirmDelete } from '../../../components/UI-Elements/FileUpload/ErrorComponents/ConfirmDelete';
import { useCurrencyRates } from '@hooks/useCurrencyRates';
export interface IBalanceTotals extends IMaterialExposure {
  balanceDate: string;
  netExposure: number;
}
const emptyBalance: IBalanceTotals = {
  balanceDate: '01/01/2024',
  futureOrder: 0,
  inventorySum: 0,
  financialPositions: 0,
  futureExpCalendar: [],
  positionExpCalendar: [],
  netExposure: 0,
};

export const BalanceOverview: React.FC<IBalanceFlowProps> = ({
  balanceFlowData: { exposureBeforeBalance, materialId, materialExposure: totalAmounts, balanceDate, mainCause },
  setCurrentStepIndex,
  routesData,
}) => {
  const { account } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const { pricesMap } = useCurrencyRates();

  const navigate = useNavigate();
  const [prevBalance, setPrevBalance] = useState<IBalanceTotals>(emptyBalance);
  const [showDetails, setShowDetails] = useState<boolean>(false);

  async function refreshCompletedBalance(accountId: string, materialId: string) {
    const completedBalance = await getOpenBalanceFlow(accountId, materialId, ['completed']);

    if (completedBalance?.id) {
      const {
        balanceDate,
        materialExposure: { futureOrder, inventorySum, financialPositions, futureExpCalendar, positionExpCalendar },
        balanceInstructions,
      } = completedBalance;

      const financialPositionsValue = balanceInstructions
        ? getFinancialPositionsSum(balanceInstructions, pricesMap[materialId]) + financialPositions
        : financialPositions;

      setPrevBalance({
        balanceDate: balanceDate.toDate().toLocaleDateString(),
        futureOrder,
        inventorySum,
        futureExpCalendar,
        positionExpCalendar,
        financialPositions: financialPositionsValue,
        netExposure: inventorySum + financialPositionsValue - futureOrder,
      });
    }
  }
  useEffect(() => {
    account.id && materialId && refreshCompletedBalance(account.id, materialId);
  }, [account, materialId]);

  const netExposure = totalAmounts.inventorySum - totalAmounts.futureOrder + totalAmounts.financialPositions;
  const [showPopup, setShowPopup] = React.useState(false);
  const [exitPopup, setExitPopup] = React.useState(false);
  const hide = () => setShowPopup(false);

  return (
    <div className="flex flex-1 flex-col gap-4">
      <h2 className="text-lg">Balance overview</h2>
      {showDetails ? (
        <>
          <BalanceOverviewDetails
            materialExposure={totalAmounts}
            prevNetExposure={prevBalance.netExposure}
            netExposure={netExposure}
          />
          <div className="flex flex-row mt-auto gap-2">
            <ButtonDesigned style="outline" onButtonClick={() => setShowDetails(false)}>
              <ChevronLeft /> Back
            </ButtonDesigned>
          </div>
        </>
      ) : (
        <>
          <MainCauseTitleSection cause={mainCause || ''} />

          <div className="flex flex-col mx-auto w-[70%]">
            <LinearGauge
              hightInRem={1.125}
              currentValue={exposureBeforeBalance}
              riskTolerance={
                getRiskTolerance(materialId, account) || createEmptyRiskTolerance(2_000_000, 1_000_000, 100_000)
              }
              numberOfTicks={12}
              additionalArrows={[]}
            />
          </div>

          <div className="flex flex-row justify-evenly mx-auto w-full gap-8">
            <BalanceChartCard
              materialExposureData={prevBalance}
              title="Previous balance"
              balanceDate={prevBalance.balanceDate}
              netExposure={prevBalance.netExposure}
              exposureColor={getExposureColor(
                getExposureStatus(getRiskTolerance(materialId, account)!, prevBalance.netExposure),
              )}
            />
            <BalanceChartCard
              materialExposureData={totalAmounts}
              netExposure={netExposure}
              title="Current balance"
              balanceDate={balanceDate.toDate().toLocaleDateString()}
              exposureColor={getExposureColor(getExposureStatus(getRiskTolerance(materialId, account)!, netExposure))}
            />
          </div>

          <div className="flex flex-row mt-auto gap-2">
            <ButtonDesigned style="outline" onButtonClick={() => setExitPopup(true)}>
              Cancel
            </ButtonDesigned>
            <ButtonDesigned
              onButtonClick={() => {
                if (routesData?.length) {
                  setCurrentStepIndex((currIndex: number) => currIndex + 1);
                } else {
                  setShowPopup(true);
                }
              }}
            >
              Next <ChevronRight />
            </ButtonDesigned>
            <div className="ml-auto">
              <ButtonDesigned disableButton={true} style="outline" onButtonClick={() => setShowDetails(true)}>
                Details
              </ButtonDesigned>
            </div>
          </div>
          <PopoverConfirmOverlay
            handleConfirm={hide}
            handleCancel={hide}
            text="We are preparing new routes for you and will notify you know once they're ready"
            title="Routes not availble yet"
            isDialogOpen={showPopup}
            showCancel={false}
          />
          {exitPopup && (
            <ConfirmDelete
              onAction={() => navigate('/dashboard')}
              onCancel={() => setExitPopup(false)}
              description="Leaving this page may result in your account remaining unbalanced. Are you sure you want to proceed?"
              title="Unsaved Changes"
              actionText="Leave"
            />
          )}
        </>
      )}
    </div>
  );
};
